import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Calendar, MapPin, Stethoscope, ChevronLeft, Inbox as InboxIcon, Archive } from "lucide-react";
import { Toaster } from "sonner";
import { api } from "../lib/api";
import { Page, EmptyState, Spinner, StatusChip, UrgencyChip } from "../components/Brand";
import { formatDateFr, add30 } from "../lib/fr";

export default function OBGYNAgenda() {
  const navigate = useNavigate();
  const [showArchived, setShowArchived] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["obgyn-agenda", showArchived],
    queryFn: async () =>
      (await api.get(`/referrals/agenda${showArchived ? "?include_archived=true" : ""}`)).data,
    refetchInterval: 30000,
  });

  const today = data?.today;
  const items = data?.items || [];

  // group by appointment_date
  const groups = items.reduce((acc, r) => {
    const k = r.appointment_date || "—";
    (acc[k] = acc[k] || []).push(r);
    return acc;
  }, {});
  const keys = Object.keys(groups).sort();

  // split future (incl. today) vs past
  const future = keys.filter((k) => k >= today);
  const past = keys.filter((k) => k < today).reverse();

  return (
    <Page>
      <Toaster position="top-center" richColors />
      <div className="flex items-center justify-between mb-6 md:mb-8 flex-wrap gap-3">
        <div>
          <div className="cs-label">OB-GYN</div>
          <h1 className="font-semibold mt-1" style={{ fontSize: 32, letterSpacing: "-0.6px" }}>Agenda</h1>
          <div className="text-[14px] mt-1" style={{ color: "var(--cs-text-muted)" }}>
            Rendez-vous programmés, du plus proche au plus lointain.
          </div>
        </div>
        <button onClick={() => navigate("/obgyn")} className="cs-btn-ghost" data-testid="back-to-inbox">
          <ChevronLeft size={16} /> <InboxIcon size={16} /> Boîte de réception
        </button>
        <button onClick={() => setShowArchived((v) => !v)}
                className={showArchived ? "cs-btn-primary text-[13px]" : "cs-btn-ghost text-[13px]"}
                data-testid="toggle-archived-agenda">
          <Archive size={14} /> {showArchived ? "Masquer archivés" : "Afficher archivés"}
        </button>
      </div>

      {isLoading ? (
        <div className="cs-card p-10 flex justify-center"><Spinner /></div>
      ) : !items.length ? (
        <EmptyState title="Agenda vide" hint="Aucun rendez-vous programmé." />
      ) : (
        <div className="flex flex-col gap-8" data-testid="agenda-list">
          {future.length > 0 && (
            <Section title="À venir" keys={future} groups={groups} today={today} onOpen={(id) => navigate(`/obgyn/manage/${id}`)} />
          )}
          {past.length > 0 && (
            <Section title="Historique" keys={past} groups={groups} today={today} onOpen={(id) => navigate(`/obgyn/manage/${id}`)} muted />
          )}
        </div>
      )}
    </Page>
  );
}

function Section({ title, keys, groups, today, onOpen, muted }) {
  return (
    <div>
      <div className="cs-label mb-3">{title}</div>
      <div className="flex flex-col gap-5">
        {keys.map((k) => (
          <DayGroup key={k} date={k} items={groups[k]} isToday={k === today} onOpen={onOpen} muted={muted} />
        ))}
      </div>
    </div>
  );
}

function DayGroup({ date, items, isToday, onOpen, muted }) {
  return (
    <div className="cs-card p-5" data-testid={`agenda-day-${date}`}>
      <div className="flex items-baseline justify-between mb-3 pb-2 border-b" style={{ borderColor: "var(--cs-border)" }}>
        <div className="flex items-center gap-2">
          <Calendar size={14} color="var(--cs-primary-dark)" />
          <h3 className="font-semibold" style={{ fontSize: 16, letterSpacing: "-0.3px" }}>
            {formatDateFr(date)}
          </h3>
          {isToday && (
            <span className="cs-chip" style={{
              background: "rgba(124,179,66,0.10)", color: "var(--cs-primary-dark)",
              borderColor: "rgba(124,179,66,0.25)",
            }} data-testid="today-chip">Aujourd'hui</span>
          )}
        </div>
        <span className="text-[12px]" style={{ color: "var(--cs-text-subtle)" }}>{items.length} RDV</span>
      </div>
      <div className="flex flex-col gap-2.5" style={{ opacity: muted ? 0.85 : 1 }}>
        {items.sort((a,b) => (a.appointment_time||"").localeCompare(b.appointment_time||""))
              .map((r) => (
          <button key={r.id}
                  onClick={() => onOpen(r.id)}
                  className="text-left p-3 rounded-[12px] flex items-center gap-4 hover:bg-[var(--cs-bg)] transition"
                  style={{ borderLeft: "3px solid var(--cs-primary)", background: "rgba(124,179,66,0.04)", opacity: r.archived ? 0.55 : 1 }}
                  data-testid={`agenda-item-${r.referral_code}`}>
            <div className="font-semibold tabular-nums" style={{ fontSize: 15, color: "var(--cs-primary-dark)", minWidth: 110 }}>
              {r.appointment_time} → {add30(r.appointment_time)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-[15px]">{r.referral_code}</span>
                <UrgencyChip value={r.urgency} />
                <StatusChip value={r.status} />
                {r.archived && (
                  <span className="cs-chip" style={{ background: "var(--cs-bg)", color: "var(--cs-text-muted)", borderColor: "var(--cs-border)" }}>Archivée</span>
                )}
              </div>
              <div className="text-[12px] mt-1 flex flex-wrap gap-x-3 gap-y-0.5" style={{ color: "var(--cs-text-muted)" }}>
                <span><MapPin size={11} className="inline mr-1" />{r.location}</span>
                <span><Stethoscope size={11} className="inline mr-1" />{r.provider}</span>
                <span>FEVG {r.ef_range} %</span>
                <span>{pregLabel(r.pregnancy_status)}</span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function pregLabel(v) {
  return ({ not_pregnant: "Non enceinte", pregnant_t1: "T1", pregnant_t2: "T2", pregnant_t3: "T3", postpartum: "Post-partum" })[v] || v;
}
